import DB from '../../private/db';
import type { IDB } from '../../private/db/interface';

const TABLE_NAME = 'ISession';
const TABLE_MAPPINGS = {
    [TABLE_NAME]: 'name, grouping, languageId, tourId, auth',
};

export interface ISessionData extends Record<string | number | symbol, string | undefined> {
    name: string;
    userState: string;
    auth: string;
}

const DEFAULT_ISESSIONDATA: ISessionData = {
    name: '',
    userState: '',
    auth: '',
};

function _instantiate(name: string = TABLE_NAME): Promise<IDB> {
    const db = DB().open(name, {
        version: 1,
        mappings: TABLE_MAPPINGS,
    });

    return db;
}

async function exists(key: string): Promise<boolean> {
    const db = await _instantiate();

    await db.open();

    const table = db.table(TABLE_NAME);
    const result = await table.get(key).catch(() => undefined);

    db.close();

    return Boolean(result);
}

async function load(key: string): Promise<ISessionData> {
    const db = await _instantiate();

    await db.open();

    const table = db.table(TABLE_NAME);
    const result = await table.get(key).catch(error => undefined);

    db.close();

    if (result === undefined) {
        throw new Error(`Unable to find data named: ${key}`);
    }

    return result as ISessionData;
}

async function save(name: string, session: ISessionData): Promise<void> {
    const db = await _instantiate();

    await db.open();

    const table = db.table(TABLE_NAME);

    const result = await table.addOrUpdate(name, session);

    db.close();

    if (!result) {
        throw new Error('Failed to save data into patch-storage table');
    }
}

async function remove(name: string): Promise<void> {
    const db = await _instantiate();

    await db.open();

    const table = db.table(TABLE_NAME);

    await table.delete(name);

    db.close();
}

function constructor(data: Partial<ISessionData>): ISessionData {
    return {
        ...DEFAULT_ISESSIONDATA,
        ...data,
    };
}

export default Object.freeze({
    load,
    save,
    exists,
    remove,
    constructor,
});
